import { Children } from 'react';
import type {
  Dispatch,
  SetStateAction,
  HTMLProps,
  ReactElement,
  ChangeEvent,
  ComponentProps,
} from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from '@shared/lib';
import type { DropdownProps } from 'react-day-picker';

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@shared/ui/select';

import { Button, buttonVariants } from '@shared/ui/button';

import { Popover, PopoverTrigger, PopoverContent } from '@shared/ui/popover';

import { ScrollArea } from '@shared/ui/scroll-area';
import { addYears, format } from 'date-fns';

export type CalendarDDProps = ComponentProps<typeof DayPicker>;

const CalendarDatePicker = ({
  className,
  classNames,
  showOutsideDays = true,
  selectTriggerClassName = '',
  ...props
}: CalendarDDProps & { selectTriggerClassName?: string }) => {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('mb-0 p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        caption_dropdowns: 'flex justify-center gap-1',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell:
          'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: 'text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100'
        ),
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today: 'bg-accent text-accent-foreground',
        day_outside: 'text-muted-foreground opacity-50',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',

        ...classNames,
      }}
      components={{
        Dropdown: ({ value, onChange, children }: DropdownProps) => {
          const options = Children.toArray(children) as ReactElement<
            HTMLProps<HTMLOptionElement>
          >[];
          const selected = options.find((child) => child.props.value === value);
          const handleChange = (value: string) => {
            const changeEvent = {
              target: { value },
            } as ChangeEvent<HTMLSelectElement>;
            onChange?.(changeEvent);
          };
          return (
            <Select
              value={value?.toString()}
              onValueChange={(value) => {
                handleChange(value);
              }}
            >
              <SelectTrigger
                className={cn(
                  ' h-[28-px] w-[200-px] py-1 pr-1.5 text-sm focus:ring-0',
                  selectTriggerClassName
                )}
              >
                <SelectValue>{selected?.props?.children}</SelectValue>
              </SelectTrigger>
              <SelectContent position="popper">
                <ScrollArea className="h-80">
                  {options.map((option, id: number) => (
                    <SelectItem
                      key={`${option.props.value}-${id}`}
                      value={option.props.value?.toString() ?? ''}
                    >
                      {option.props.children}
                    </SelectItem>
                  ))}
                </ScrollArea>
              </SelectContent>
            </Select>
          );
        },
        IconLeft: () => <ChevronLeftIcon className="h-4 w-4" />,
        IconRight: () => <ChevronRightIcon className="h-4 w-4" />,
      }}
      {...props}
    />
  );
};
CalendarDatePicker.displayName = 'Calendar';

const DatePicker = ({
  date,
  setDate,
  className,
  buttonTriggerClassName,
  disabled,
}: {
  date: Date;
  setDate: Dispatch<SetStateAction<Date | undefined>>;
  className?: string;
  disabled?: boolean;
  buttonTriggerClassName?: string;
}) => {
  const maxYear = addYears(new Date(), 10).getFullYear();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          className={cn(
            'h-9 w-[250px] justify-start text-left font-normal',
            !date && 'text-muted-foreground',
            className,
            buttonTriggerClassName
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, 'P') : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <CalendarDatePicker
          mode="single"
          captionLayout="dropdown-buttons"
          selected={date}
          onSelect={setDate}
          fromYear={1900}
          toYear={maxYear}
        />
      </PopoverContent>
    </Popover>
  );
};

export { CalendarDatePicker, DatePicker };
